import {
  Badge,
  Box,
  Checkbox,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { NotInRoundBadge } from "./NotInRoundBadge";
import { ConfirmedUnchangedBadge } from "./ConfirmedUnchangedBadge";
import { useTranslation } from "react-i18next";
import { BidStatus, type EvalBidDto } from "../../../autogen/bff-api";
import { MinimumRequirementOutcomeBadge } from "../minimum-requirements/MinimumRequirementOutcomeBadge";
import { PriceCell } from "./PriceCell";
import { PriceChange, showsChangeSincePrevious } from "./PriceChange";

interface Props {
  bids: EvalBidDto[];
  readOnly: boolean;
  awarded: boolean;
  selectedBidIds: string[];
  onToggleSelection: (bidId: string) => void;
  onPriceOverride: (
    bidId: string,
    value: number | null,
    comment: string | undefined,
    forDeliverySubmittedAt: string | undefined,
  ) => Promise<void>;
}

/**
 * ADR-033 D3: the price evaluation model — events without award criteria rank by
 * effective price alone. One row per bid in rank order; the price is override-aware
 * and, in a round context, shown against the supplier's original bid. During
 * evaluation the buyer marks the selected bid(s) — the award's default winners.
 */
export const PriceEvaluationView = ({
  bids,
  readOnly,
  awarded,
  selectedBidIds,
  onToggleSelection,
  onPriceOverride,
}: Props) => {
  const { t } = useTranslation();
  const showChange = bids.some((bid) => bid.originalPrice?.value != null);
  const showPrevious = showsChangeSincePrevious(bids);

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
      <TableContainer>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th w="12">#</Th>
              {!awarded && <Th w="12">{t("Selected")}</Th>}
              <Th>{t("Supplier")}</Th>
              <Th textAlign="right">{t("Price")}</Th>
              {showPrevious && <Th textAlign="right">{t("Change since previous bid")}</Th>}
              {showChange && <Th textAlign="right">{t("Change since original bid")}</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {bids.map((bid) => {
              const isRejected = bid.status === BidStatus.Rejected;
              const isSelected = awarded
                ? bid.status === BidStatus.Awarded
                : selectedBidIds.includes(bid.bidId);
              return (
                <Tr key={bid.bidId} bg={isRejected ? "red.50" : isSelected ? "green.50" : undefined}>
                  <Td fontWeight="bold" fontFamily="mono" color={isRejected ? "red.700" : "smMuted"}>
                    {isRejected || bid.rank <= 0 ? "–" : bid.rank}
                  </Td>
                  {!awarded && (
                    <Td>
                      <Checkbox
                        aria-label={`${t("Selected")}: ${bid.supplierName}`}
                        colorScheme="green"
                        isChecked={isSelected}
                        isDisabled={readOnly || isRejected}
                        onChange={() => onToggleSelection(bid.bidId)}
                      />
                    </Td>
                  )}
                  <Td fontWeight="medium" color="smText">
                    {bid.supplierName || t("Unknown")}{" "}
                    {isRejected && <Badge colorScheme="red">{t("Avvist")}</Badge>}
                    <NotInRoundBadge notInRound={bid.notInRound} />
                    <ConfirmedUnchangedBadge confirmedUnchanged={bid.confirmedUnchanged} />
                    {!isRejected && (
                      <MinimumRequirementOutcomeBadge minimumRequirements={bid.minimumRequirements} />
                    )}
                    {isSelected && <Badge colorScheme="green">{t("Selected")}</Badge>}
                  </Td>
                  <Td textAlign="right">
                    <PriceCell
                      bid={bid}
                      readOnly={readOnly || isRejected}
                      onSave={(value, comment, forDelivery) => onPriceOverride(bid.bidId, value, comment, forDelivery)}
                    />
                  </Td>
                  {showPrevious && (
                    <Td textAlign="right">
                      <PriceChange from={bid.previousPrice} to={bid.effectivePrice} />
                    </Td>
                  )}
                  {showChange && (
                    <Td textAlign="right">
                      <PriceChange from={bid.originalPrice} to={bid.effectivePrice} />
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
